import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { injectIntl, IntlProvider } from 'react-intl';

import config, { getCurrentLanguage } from '../containers/LanguageSwitcher/config';
import AppLocale from '../languageProvider';
import translateErrorFields from './translateErrorFields';
import { basePath } from '../v2/baseRouter';

const redirectToLogin = () => {
  window.location.href = basePath;
};

const formatValidationMessageValue = (field, value) => {
  if (value === null || typeof value === 'undefined') return [];

  if (Array.isArray(value)) {
    return value.reduce((messages, row) => {
      const message = String(row);
      if (!field || message.includes('$(')) return messages.concat(message);
      return messages.concat(`${field}: ${message}`);
    }, []);
  }

  const message = String(value);
  if (!field || message.includes('$(')) return [message];
  return [`${field}: ${message}`];
};

const parseValidationMessages = msg => {
  if (msg === null || typeof msg === 'undefined') return [];

  try {
    const parsed = JSON.parse(msg);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).reduce((messages, key) => {
        return messages.concat(formatValidationMessageValue(key, parsed[key]));
      }, []);
    }
    return [String(parsed)];
  } catch (e) {
    return (Array.isArray(msg) ? msg : String(msg).split(',')).filter(Boolean);
  }
};

const formatValidationMessages = (intl, msg, moduleName, functionName) => {
  const validationMessages = parseValidationMessages(msg);
  if (moduleName) {
    return translateErrorFields(intl, validationMessages, moduleName, functionName);
  }
  return validationMessages.map(errorMessage =>
    errorMessage.replace(/\$\((.*?)\)/g, (matched, errorField) => ` [${errorField}] `),
  );
};

const ErrorContent = ({ intl, code, msg, moduleName = null, functionName = null }) => {
  const isMMRMErrorCode = /^0x[\dA-F]{8}$/.test(code);

  const errorCode = isMMRMErrorCode && `代碼： ${code}`;

  let errorMessage = [];
  if (isMMRMErrorCode && code === '0x00000002') {
    const validationMessages = formatValidationMessages(intl, msg, moduleName, functionName);
    errorMessage.push('欄位驗證錯誤');
    validationMessages.forEach(value => {
      errorMessage.push(<div>{value}</div>);
    });
  } else if (isMMRMErrorCode) {
    errorMessage = intl.formatMessage({ id: `error.${code}` });
  } else {
    errorMessage.push(<br />);
    errorMessage.push('-------------------------------------');
    errorMessage.push(<br />);
    const msgs = Array.isArray(msg) ? msg : msg.split(',');
    msgs.forEach(value => {
      errorMessage.push(<div>{value}.</div>);
    });
  }

  return (
    <span>
      {errorCode}
      <br />
      訊息：
      {errorMessage}
      <br />
    </span>
  );
};

const IntlInjectedErrorContent = injectIntl(ErrorContent);

const currentAppLocale = AppLocale[getCurrentLanguage(config.defaultLanguage || 'english').locale];

ErrorContent.propTypes = {
  code: PropTypes.string.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  msg: PropTypes.string,
};

ErrorContent.defaultProps = {
  msg: null,
};

const ERROR_CODES_SHOULD_TRIGGER_REDIRECT = ['0x00000004', '0x00000005'];

export function handleError(error, moduleName = null, functionName = null) {
  console.error(`API error with status code ${error.code} ${error.message}`);
  const { code } = error;
  Modal.error({
    title: '發生錯誤',
    content: (
      <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
        <IntlInjectedErrorContent
          code={code}
          msg={error.message}
          moduleName={moduleName}
          functionName={functionName}
        />
      </IntlProvider>
    ),
    onOk: () => {
      if (ERROR_CODES_SHOULD_TRIGGER_REDIRECT.includes(code)) {
        redirectToLogin();
      }
    },
  });
}

export default handleError;
